// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import SyncProblemRoundedIcon from '@mui/icons-material/SyncProblemRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';

// material ui
import {
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Divider,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import { usePatchSensorMutation } from '@/api/recalc/endpoints/sensorsApiSlice';

// shared components
import { WritableOnly } from '@/components/access-control';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// Data interval enum options
const DATA_INTERVAL_OPTIONS = [
  // { value: 'Second', label: 'Second', description: '1s' },
  { value: 'Seconds3Boost', label: 'Seconds3Boost', description: '3s' },
  { value: 'Seconds20Boost', label: 'Seconds20Boost', description: '20s' },
  { value: 'Minute', label: 'Minute', description: '1 min' },
  { value: 'Minutes5', label: 'Minutes5', description: '5 min' },
  { value: 'Minutes30', label: 'Minutes30', description: '30 min' },
  // { value: 'Inactive', label: 'Inactive', description: 'Inactive' },
];

// Helper function to get display name for interval enum value
const getIntervalLabel = (enumValue, sensor) => {
  if (sensor?.SensorType === 'GatewaySensor') {
    return 'Minute';
  }
  if (enumValue == null) return '—';
  const option = DATA_INTERVAL_OPTIONS.find(
    (opt) => opt.value === String(enumValue),
  );
  return option ? option.label : String(enumValue);
};

/**
 * DataIntervalStatus
 *
 * Props:
 * - sensorId: number|string - sensor ID for updating (required for editing)
 * - dataInterval: number|string|null|undefined  (current interval)
 * - targetDataInterval: number|string|null|undefined (desired interval)
 * - variant: Typography variant for the text (default: "body1")
 * - showText: whether to render the interval text (default: true)
 * - size: icon size (default: "small")
 * - editable: whether to allow editing (default: false)
 */
export function DataIntervalStatus({
  sensorId,
  sensor,
  variant = 'body1',
  showText = true,
  size = 'small',
  editable = false,
}) {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingInterval, setPendingInterval] = useState(null);
  const [patchSensor, { isLoading }] = usePatchSensorMutation();

  const { t } = useTranslation(['units-gateways-sensors', 'common']);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (!editable || !sensorId) return;
    event.stopPropagation();
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setPendingInterval(null);
  };

  const handleIntervalSelect = (newInterval) => {
    setPendingInterval(newInterval);
  };

  const handleConfirmChange = async () => {
    if (!sensorId || !pendingInterval) return;

    const result = await patchSensor({
      id: sensorId,
      patch: { DataInterval: pendingInterval },
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message:
            t?.('sensors.dataIntervalUpdated', {
              interval: getIntervalLabel(pendingInterval, sensor),
            }) ||
            `Data interval updated to ${getIntervalLabel(pendingInterval, sensor)}`,
          severity: 'success',
        }),
      );
      handleClose();
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };
  const hasCurrent = sensor?.DataInterval != null;
  const hasTarget = sensor?.TargetDataInterval != null;

  const changeInProgress =
    hasTarget && sensor?.DataInterval !== sensor?.TargetDataInterval;
  const ok = hasTarget
    ? sensor?.DataInterval === sensor?.TargetDataInterval
    : true;

  // "Problem" heuristic:
  // - mismatch but no target (can't confirm intent) => red
  // - or missing current value => red
  const problem = !hasCurrent || (!hasTarget && hasCurrent && !ok);

  const showIcon = changeInProgress || problem;

  const getStatusIcon = () => {
    if (sensor?.SensorType === 'GatewaySensor') {
      return null;
    }
    if (changeInProgress) {
      return (
        <Tooltip
          title={t('units-gateways-sensors:tooltips.changingInterval', {
            interval: sensor?.targetDataInterval,
          })}
          arrow
        >
          <SyncRoundedIcon
            fontSize={size}
            sx={{
              color: 'info.main',
              '@keyframes spin': {
                from: { transform: 'rotate(0deg)' },
                to: { transform: 'rotate(-360deg)' },
              },
              animation: 'spin 3s linear infinite',
            }}
          />
        </Tooltip>
      );
    }
    if (problem) {
      return (
        <Tooltip
          title={t('units-gateways-sensors:tooltips.dataIntervalMismatch')}
          arrow
        >
          <SyncProblemRoundedIcon
            fontSize={size}
            sx={{
              color: 'error.main',
            }}
          />
        </Tooltip>
      );
    }
    return null;
  };

  const dataIntervalDisabled = sensor?.SensorType === 'GatewaySensor';

  return (
    <>
      {/* {sensor?.SensorType === 'GatewaySensor' ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ minWidth: 0 }}
        >
          <Typography
            variant={variant}
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            {t('sensors.gatewaySensorInterval', 'Minute')}
          </Typography>
        </Stack>
      ) : ( */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ minWidth: 0 }}
      >
        {showText && !editable && (
          <Typography variant={variant} sx={{ fontWeight: 500 }} noWrap>
            {hasCurrent ? String(sensor?.DataInterval) : '—'}
          </Typography>
        )}
        {editable && sensorId ? (
          <Tooltip
            title={
              !dataIntervalDisabled
                ? t(
                    'sensors.editDataInterval',
                    'Update Data Interval - this process can take up to a few minutes',
                  )
                : t(
                    'sensors.editDataIntervalDisabled',
                    'Data Interval cannot be changed for Gateway Sensors',
                  )
            }
            placement="right"
          >
            <span>
              <Button
                variant="outlined"
                startIcon={showIcon && getStatusIcon()}
                endIcon={<ArrowDropDownRoundedIcon />}
                onClick={handleClick}
                disabled={dataIntervalDisabled}
                sx={{
                  minWidth: '140px',
                  maxWidth: '140px',
                  px: 1.5,
                  py: 0.25,
                  fontSize: variant === 'body2' ? '0.875rem' : '1rem',
                  textTransform: 'none',
                  borderColor: 'divider',
                  color: 'text.primary',
                  justifyContent: 'space-between',
                  '& .MuiButton-startIcon': {
                    marginRight: 0.5,
                  },
                  '& .MuiButton-endIcon': {
                    marginLeft: 'auto',
                    marginRight: 0,
                  },
                  '&:hover': {
                    borderColor: 'divider',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  {getIntervalLabel(sensor?.DataInterval, sensor)}
                </Box>
              </Button>
            </span>
          </Tooltip>
        ) : (
          showText &&
          editable && (
            <Typography variant={variant} sx={{ fontWeight: 500 }} noWrap>
              {hasCurrent
                ? getIntervalLabel(sensor?.DataInterval, sensor)
                : '—'}
            </Typography>
          )
        )}
      </Stack>
      {/* )} */}

      {/* Popper menu for editing */}
      {editable && sensorId && (
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Paper
              elevation={8}
              sx={{
                minWidth: 200,
                maxWidth: 280,
                mt: 1,
                p: 1,
              }}
            >
              <WritableOnly>
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('sensors.current')}:{' '}
                    {hasCurrent
                      ? `${sensor?.DataInterval}`
                      : t('common.notAvailable')}
                  </Typography>
                  {hasTarget && changeInProgress && (
                    <Typography
                      variant="caption"
                      color="info.main"
                      display="block"
                    >
                      {t('sensors.target')}: {sensor?.TargetDataInterval}
                    </Typography>
                  )}
                </Box>
                <Divider />
                {isLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 3,
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <MenuList dense>
                    {DATA_INTERVAL_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.value}
                        onClick={() => handleIntervalSelect(option.value)}
                        sx={{
                          borderRadius: 1,
                        }}
                        selected={
                          pendingInterval
                            ? String(pendingInterval) === option.value
                            : sensor?.TargetDataInterval
                              ? String(sensor?.TargetDataInterval) ===
                                option.value
                              : String(sensor?.DataInterval) === option.value
                        }
                      >
                        <Typography variant="body2">{option.label}</Typography>
                      </MenuItem>
                    ))}
                  </MenuList>
                )}
                {!isLoading && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ px: 1, pb: 0.5 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        disableElevation
                        size="small"
                        onClick={handleConfirmChange}
                        disabled={dataIntervalDisabled}
                        sx={{
                          textTransform: 'none',
                        }}
                      >
                        {t('actions.confirm', { ns: 'common' })}
                      </Button>
                    </Box>
                  </>
                )}
              </WritableOnly>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
}
